import { convertBedrockBlock } from '../nbt/bedrockBlocks';
import { blockLabel } from '../nbt/blockLabel';
import { gameItemInfo, registerGameItem } from '../nbt/gameCatalog';
import { educationTextureFile, type EducationCatalog, type EducationRegistry } from './educationCatalog';
import { resourceId } from './javaModels';

const categories: Record<string, string> = { construction: '構築', nature: '自然', equipment: '所持品', items: 'アイテム' };

/** Register items as well as blocks, using Education's own inventory and language data. */
export function registerEducationNames(catalog: EducationCatalog) {
  const names = new Set([...Object.keys(catalog.blocks).map(resourceId), ...Object.keys(catalog.inventory)]);
  for (const id of names) {
    const raw = id.replace(/^minecraft:/, '');
    const mapped = convertBedrockBlock(raw, {}).name;
    const item = catalog.inventory[id] ?? catalog.inventory[mapped];
    const serialization = catalog.ids?.[id] ?? catalog.itemIds?.[id];
    const name = (serialization ? catalog.language[serialization + '.name'] : undefined)
      ?? catalog.language[`tile.${raw}.name`] ?? catalog.language[`tile.${raw}.default.name`] ?? catalog.language[`item.${raw}.name`];
    const info = { name, category: item ? categories[item.category] ?? item.category : undefined, group: item ? catalog.language[item.group] : undefined };
    registerGameItem(id, info);
    // Item aliases must not overwrite the label of a different block (e.g. buckets).
    if (raw in catalog.blocks) registerGameItem(mapped, info);
  }
}

/** Inventory sprites are separate from placed-block models. Unknown add-ons stay unknown. */
export function educationItemTexture(registry: EducationRegistry, name: string): string | null {
  const id = resourceId(name);
  if (!id.startsWith('minecraft:')) return null;
  const raw = id.slice('minecraft:'.length);
  const icon = registry.catalog.itemIcons?.[raw]?.textures;
  const first = Array.isArray(icon) ? icon[0] : icon;
  const path = typeof first === 'string' ? first : first?.path;
  if (path && educationTextureFile(registry, 'edu:' + path)) return 'edu:' + path;
  // Java's item models also give the filename when it differs from the item's ID.
  const model = registry.models.get('minecraft:item/' + raw);
  const layer = model?.textures?.layer0;
  const texture = typeof layer === 'string' && !layer.startsWith('#') ? resourceId(layer) : 'minecraft:item/' + raw;
  return educationTextureFile(registry, texture) ? texture : null;
}

export interface EducationMaterial {
  id: string;
  name: string;
  kind: 'block' | 'item';
  category?: string;
  group?: string;
}

export function educationMaterials(registry: EducationRegistry): EducationMaterial[] {
  registerEducationNames(registry.catalog);
  const ids = new Set([...Object.keys(registry.catalog.inventory), ...Object.keys(registry.catalog.blocks).map(name => convertBedrockBlock(name, {}).name)]);
  return [...ids].map(id => {
    const canonical = resourceId(id);
    const isBlock = registry.native.has(canonical) || registry.states.has(canonical);
    return { id: canonical, name: blockLabel(canonical), kind: isBlock ? 'block' as const : 'item' as const, ...gameItemInfo(canonical) };
  }).sort((a, b) => a.name.localeCompare(b.name, 'ja') || a.id.localeCompare(b.id));
}

const searchText = (value: string) => value.normalize('NFKC').toLowerCase().replace(/[ァ-ヶ]/g, char => String.fromCharCode(char.charCodeAt(0) - 0x60));
export function searchMaterials(materials: EducationMaterial[], query: string, kind: 'all' | EducationMaterial['kind'] = 'all'): EducationMaterial[] {
  const terms = searchText(query).trim().split(/\s+/).filter(Boolean);
  return materials.filter(item => {
    if (kind !== 'all' && item.kind !== kind) return false;
    const reading = item.id === 'minecraft:spyglass' ? 'ぼうえんきょう' : '';
    const text = searchText([item.id, item.name, item.category, item.group, reading].join(' '));
    return terms.every(term => text.includes(term));
  });
}
